import { apiClient } from '../Api';
import { BaseService } from './BaseService';

export interface DiscoveryQuotaInfo {
  groupId?: string;
  groupName: string;
  groupDescription: string;
  maxConsultations: number;
  maxDailyDownloads: number;
}

export interface UserFormulaInfo {
  groupName: string;
  groupDescription: string;
  maxConsultations: number;
  consultationsUsed: number;
  isUnlimitedConsultations: boolean;
  maxDailyDownloads: number;
  maxWeeklyDownloads: number;
  maxMonthlyDownloads: number;
}

export interface PublicFormulaItem {
  id: string;
  label: string;
  description: string;
  groupCategory: string;
  maxConsultations: number;
  maxDailyDownloads: number;
  maxWeeklyDownloads: number;
  maxMonthlyDownloads: number;
  permissions?: string[];
}

class FormulaService extends BaseService {
  private discoveryPromise: Promise<DiscoveryQuotaInfo> | null = null;
  private cachedDiscoveryQuota: DiscoveryQuotaInfo | null = null;

  constructor() {
    super('/api/groups');
  }

  /**
   * Récupère le quota dynamique configuré pour la formule Découverte (avec cache en mémoire)
   */
  async getDiscoveryQuota(forceRefresh = false): Promise<DiscoveryQuotaInfo> {
    if (!forceRefresh && this.cachedDiscoveryQuota) {
      return this.cachedDiscoveryQuota;
    }

    if (!forceRefresh && this.discoveryPromise) {
      return this.discoveryPromise;
    }

    this.discoveryPromise = (async () => {
      try {
        const res = await apiClient.get<DiscoveryQuotaInfo>('/api/groups/discovery-quota');
        this.cachedDiscoveryQuota = res;
        return res;
      } catch (err) {
        console.warn('Impossible de charger le quota Découverte, utilisation de la valeur par défaut', err);
        const fallback: DiscoveryQuotaInfo = {
          groupName: 'Découverte',
          groupDescription: 'Formule d\'accueil gratuite à l\'inscription',
          maxConsultations: 5,
          maxDailyDownloads: 0
        };
        this.cachedDiscoveryQuota = fallback;
        return fallback;
      } finally {
        this.discoveryPromise = null;
      }
    })();

    return this.discoveryPromise;
  }

  /**
   * Récupère la formule et le quota du compte connecté
   */
  async getMyFormula(): Promise<UserFormulaInfo | null> {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (!token) return null;

    try {
      return await apiClient.get<UserFormulaInfo>('/api/groups/my-formula');
    } catch (err) {
      console.warn('Impossible de charger la formule de l\'utilisateur connecté', err);
      return null;
    }
  }

  /**
   * Récupère les formules publiques configurées par l'administrateur
   */
  async getPublicFormulas(): Promise<PublicFormulaItem[]> {
    try {
      const res = await apiClient.get<PublicFormulaItem[]>('/api/groups/public-formulas');
      return Array.isArray(res) ? res : [];
    } catch (err) {
      console.warn('Impossible de charger les formules publiques', err);
      return [];
    }
  }

  /**
   * Invalide le cache local (après mise à jour par l'administrateur par exemple)
   */
  clearCache(): void {
    this.cachedDiscoveryQuota = null;
    this.discoveryPromise = null;
  }
}

export const formulaService = new FormulaService();
